import type { ButtonProps } from '@ariakit/react';
import { Button as AriakitButton } from '@ariakit/react';
import type { ClassValue } from 'clsx';
import clsx from 'clsx';

import { Box } from '../Box/Box';
import type { IconProps } from '../icons';
import type { Props as TextProps } from '../Text/Text';
import { Text } from '../Text/Text';

import * as styles from './styles.css';

export type Props = ButtonProps &
  styles.Variants & {
    /** An alias for iconLeft as a convenience for icon-only tags. */
    icon?: React.ReactElement<IconProps>;
    iconLeft?: React.ReactElement<IconProps>;
    iconRight?: React.ReactElement<IconProps>;
    className?: ClassValue | ClassValue[];
    lines?: TextProps['lines'];
    wordBreak?: TextProps['break'];
    onIconLeftClick?: () => void;
    onIconRightClick?: () => void;
  } & {
    allowOverflow?: boolean;
    centerText?: boolean;
  };

const buttonToTextSize = {
  small: 'xSmall',
  medium: 'xSmall',
  large: 'small',
} as const;

export const Tag: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  className,
  icon,
  iconLeft,
  iconRight,
  emphasis = styles.defaultEmphasis,
  shape = styles.defaultShape,
  size = styles.defaultSize,
  kind = styles.defaultKind,
  lines,
  wordBreak,
  onIconLeftClick,
  onIconRightClick,
  allowOverflow = false,
  centerText = false,
  'aria-label': ariaLabel,
  ...buttonProps
}) => {
  const textSize: TextProps['size'] = buttonToTextSize[size];
  icon = icon || iconLeft;

  return (
    <AriakitButton
      render={<button aria-label={ariaLabel} />}
      className={clsx([
        className,
        styles.variants({
          emphasis,
          kind,
          size,
          shape,
        }),
      ])}
      style={{ cursor: buttonProps.onClick ? 'pointer' : 'default' }}
      type="button"
      aria-label={ariaLabel}
      {...buttonProps}
    >
      {icon && (
        <Box as="span" display="inline-flex" cssClass={styles.iconVariants({ size })} onClick={onIconLeftClick}>
          {icon}
        </Box>
      )}
      {children && (
        <Box
          as="span"
          display="inline-flex"
          style={{
            minWidth: 0,
            flex: '1 1 auto',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: allowOverflow ? 'normal' : 'nowrap',
            justifyContent: centerText ? 'center' : undefined,
          }}
        >
          <Text lines={lines} size={textSize} userSelect="none" display="inline-flex" break={wordBreak}>
            {children}
          </Text>
        </Box>
      )}
      {iconRight && (
        <Box as="span" display="inline-flex" cssClass={styles.iconVariants({ size })} onClick={onIconRightClick}>
          {iconRight}
        </Box>
      )}
    </AriakitButton>
  );
};
